import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardAlertes } from '../services/api';

const TABS = [
  { key: 'arc',    label: '🚧 ARC à traiter',     emptyMsg: 'Aucun ARC en attente',     counterMode: 'until', redThreshold: 3, hideIfPast: false, hideIfFuture: false },
  { key: 'dostec', label: '⏳ DOSTEC en attente',  emptyMsg: 'Aucun DOSTEC en attente',  counterMode: 'since', redThreshold: 4, hideIfPast: false, hideIfFuture: false },
  { key: 'nacelle',label: '🏗️ Nacelle nécessaire', emptyMsg: 'Aucune nacelle prévue',    counterMode: 'until', redThreshold: 3, hideIfPast: true,  hideIfFuture: false },
];

export default function SuiviTerrain() {
  const [activeTab, setActiveTab] = useState('arc');
  const [alertes, setAlertes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDashboardAlertes();
      setAlertes(data);
      setLastRefresh(new Date());
    } catch {
      setAlertes({ arc: [], dostec: [], nacelle: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tab = TABS.find(t => t.key === activeTab);
  const items = alertes?.[activeTab] || [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = items.filter(item => {
    if (!item.date) return true;
    const d = new Date(item.date);
    d.setHours(0, 0, 0, 0);
    if (tab.hideIfPast && d < today) return false;
    if (tab.hideIfFuture && d >= today) return false;
    return true;
  });

  const getCounter = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (tab.counterMode === 'until') {
      if (diff < 0) return null;
      return { days: diff, red: diff <= tab.redThreshold, label: `J-${diff}` };
    } else {
      if (diff >= 0) return null;
      const abs = Math.abs(diff);
      return { days: abs, red: abs > tab.redThreshold, label: `J+${abs}` };
    }
  };

  const counts = alertes ? {
    arc: (alertes.arc || []).filter(item => {
      if (!item.date) return true;
      const d = new Date(item.date); d.setHours(0,0,0,0);
      return true;
    }).length,
    dostec: (alertes.dostec || []).filter(item => {
      if (!item.date) return true;
      const d = new Date(item.date); d.setHours(0,0,0,0);
      return d < today;
    }).length,
    nacelle: (alertes.nacelle || []).filter(item => {
      if (!item.date) return true;
      const d = new Date(item.date); d.setHours(0,0,0,0);
      return d >= today;
    }).length,
  } : null;

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Suivi Terrain</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            Interventions nécessitant une action — données Zoho
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && (
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              Actualisé à {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            ↻ Actualiser
          </button>
        </div>
      </div>

      <div className="page">

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {TABS.map(t => {
            const count = counts?.[t.key];
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                  color: isActive ? 'var(--text)' : 'var(--text3)',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 13,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: -1,
                  transition: 'color .15s',
                }}
              >
                {t.label}
                {count != null && (
                  <span style={{
                    background: count > 0 ? 'var(--accent)' : 'var(--bg3)',
                    color: count > 0 ? '#fff' : 'var(--text3)',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '1px 7px',
                    minWidth: 20,
                    textAlign: 'center',
                  }}>
                    {loading ? '…' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Contenu onglet actif */}
        <div className="card">
          {/* En-tête */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{tab.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
                {tab.key === 'arc'    && 'Interventions avec arrêté de circulation nécessaire'}
                {tab.key === 'dostec' && 'Interventions avec dossier DOSTEC à déposer'}
                {tab.key === 'nacelle' && 'Interventions nécessitant une nacelle élévatrice'}
              </div>
            </div>
            {!loading && (
              <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 600 }}>
                {filtered.length} intervention{filtered.length > 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Tableau */}
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              Chargement depuis Zoho…
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="icon">✓</div>
              <p>{tab.emptyMsg}</p>
            </div>
          ) : (
            <>
              {/* Header tableau */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.2fr 0.7fr 1fr 1.5fr 0.8fr 1fr 0.6fr 80px',
                gap: 8,
                padding: '6px 12px',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                borderBottom: '1px solid var(--border)',
                marginBottom: 4,
              }}>
                <div>Réf commande</div>
                <div>Client</div>
                <div>Typo</div>
                <div>Technicien</div>
                <div>Adresse</div>
                <div>Date / Heure</div>
                <div>Étape</div>
                <div style={{ textAlign: 'center' }}>Délai</div>
                <div style={{ textAlign: 'center' }}>Zoho</div>
              </div>

              {/* Lignes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filtered.map(item => {
                  const counter = getCounter(item.date);
                  return (
                    <div key={item.id} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1.2fr 0.7fr 1fr 1.5fr 0.8fr 1fr 0.6fr 80px',
                      gap: 8,
                      padding: '10px 12px',
                      background: 'var(--bg3)',
                      borderRadius: 8,
                      alignItems: 'center',
                      fontSize: 12,
                    }}>
                      <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--blue2)', fontSize: 11 }}>
                        {item.ref_commande || '—'}
                      </div>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.client}>
                        {item.client || '—'}
                      </div>
                      <div>
                        {item.typo ? (
                          <span className="badge badge-blue" style={{ fontSize: 10 }}>{item.typo}</span>
                        ) : '—'}
                      </div>
                      <div style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.technicien}>
                        {item.technicien || '—'}
                      </div>
                      <div style={{ color: 'var(--text3)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.adresse}>
                        {item.adresse || '—'}
                      </div>
                      <div style={{ color: 'var(--text2)', fontSize: 11 }}>
                        {item.date ? new Date(item.date).toLocaleDateString('fr-FR') : '—'}
                        {item.heure && <div style={{ color: 'var(--text3)' }}>{item.heure}</div>}
                      </div>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.stage}>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: 6,
                          background: 'var(--bg2)',
                          color: 'var(--text2)',
                        }}>
                          {item.stage || '—'}
                        </span>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        {counter !== null ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 9px',
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            background: counter.red ? '#fef2f2' : '#f0fdf4',
                            color: counter.red ? '#ef4444' : 'var(--green2)',
                          }}>
                            {counter.label}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Link
                          to={`/dossier/${item.id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 11, padding: '4px 10px' }}
                        >Fiche ↗</Link>
                        <a
                          href={`https://crm.zoho.eu/crm/tab/Potentials/${item.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 11, padding: '4px 10px' }}
                        >
                          Zoho →
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Légende compteur */}
        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 20 }}>
          {tab.counterMode === 'until' && (
            <>
              <span><span style={{ color: 'var(--green2)', fontWeight: 700 }}>J-X</span> — jours avant l'intervention</span>
              <span><span style={{ color: '#ef4444', fontWeight: 700 }}>Rouge</span> — ≤ {tab.redThreshold} jours</span>
            </>
          )}
          {tab.counterMode === 'since' && (
            <>
              <span><span style={{ color: 'var(--green2)', fontWeight: 700 }}>J+X</span> — jours depuis l'intervention</span>
              <span><span style={{ color: '#ef4444', fontWeight: 700 }}>Rouge</span> — &gt; {tab.redThreshold} jours</span>
            </>
          )}
        </div>

      </div>
    </>
  );
}
